import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  PrivacyTip as PrivacyTipIcon,
  Cookie as CookieIcon,
} from '@mui/icons-material';

const Privacy = () => {
  const theme = useTheme();

  const privacySections = [
    {
      title: 'Information We Collect',
      icon: <PrivacyTipIcon sx={{ color: 'primary.main' }} />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            We collect information you provide directly to us, such as when you:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
            <li>Fill out contact forms on our website</li>
            <li>Subscribe to our newsletter</li>
            <li>Request a quote or proposal</li>
            <li>Apply for employment opportunities</li>
            <li>Contact us for support or inquiries</li>
          </Typography>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            This information may include your name, email address, phone number, company name, 
            and any other information you choose to provide.
          </Typography>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            <strong>Mobile Applications (Leru):</strong> Our Leru weather app has no user
            accounts and contains no advertising or third-party analytics or tracking SDKs.
            Your favourites and settings are stored only on your device. In the course of
            providing forecasts, the app does exchange some data:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
            <li>
              To return forecasts, the app sends the coordinates — and, for places you search
              for or pin, the name you enter — of the locations you look up to our weather
              service. As with any internet request, these include your device's IP address.
              Locations you request may be retained by our service so their forecast data can
              be kept up to date.
            </li>
            <li>
              With your permission, the app may use your device's approximate (coarse) location
              for the single purpose of centring the weather map near you. This is optional,
              is processed on your device, and is not used to track you.
            </li>
            <li>
              The map is drawn with tiles from OpenStreetMap. When you view the map your device
              contacts OpenStreetMap's servers directly, which receive your IP address and the
              map area you are viewing, under OpenStreetMap's own privacy policy. Weather data
              is provided by Open-Meteo.
            </li>
            <li>
              Weather alerts are delivered as local notifications generated on your device; we
              do not operate a push-notification server and do not collect a device token.
            </li>
          </Typography>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            <strong>Mobile Applications (WingLog):</strong> Our WingLog app has no user
            accounts and contains no advertising or third-party analytics or tracking SDKs. It
            records your flight for your own use, and your data stays on your device:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
            <li>
              With your permission, the app reads your device's precise location to show your
              live speed, altitude and position and to record the breadcrumb trail of your
              flight. While you are recording, an ongoing notification keeps this running with
              the screen off. You can stop recording at any time.
            </li>
            <li>
              Your recorded flights, settings and any offline data are stored only on your
              device. We do not upload, sell or share them. They leave the device only if you
              choose to share or export a flight (for example as an image or a GPX file), in
              which case it goes to the app or person you select.
            </li>
            <li>
              The moving map is drawn with OpenStreetMap tiles served through our own caching
              proxy (osmtiles.teniquasystems.com). When you view the map, your device requests
              map tiles for the area you are viewing; as with any internet request these include
              your device's IP address. We do not use these requests to build a profile of you.
              Map data is © OpenStreetMap contributors.
            </li>
            <li>
              Ground-elevation lookups use terrain data you download in advance; once downloaded,
              those lookups happen entirely on your device with no further network requests.
            </li>
          </Typography>
        </Box>
      ),
    },
    {
      title: 'How We Use Your Information',
      icon: <SecurityIcon sx={{ color: 'primary.main' }} />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            We use the information we collect to:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
            <li>Provide, maintain, and improve our services</li>
            <li>Respond to your comments, questions, and requests</li>
            <li>Send you technical notices, updates, and support messages</li>
            <li>Communicate with you about products, services, and events</li>
            <li>Monitor and analyze trends, usage, and activities</li>
            <li>Detect, investigate, and prevent fraudulent transactions</li>
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Cookies and Tracking Technologies',
      icon: <CookieIcon sx={{ color: 'primary.main' }} />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            We use cookies and similar tracking technologies to track activity on our website 
            and hold certain information. Cookies are files with a small amount of data which 
            may include an anonymous unique identifier.
          </Typography>
          <Typography variant="body1" paragraph>
            You can instruct your browser to refuse all cookies or to indicate when a cookie 
            is being sent. However, if you do not accept cookies, you may not be able to use 
            some portions of our website.
          </Typography>
          <Typography variant="body1" paragraph>
            We use the following types of cookies:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
            <li><strong>Essential cookies:</strong> Required for the website to function properly</li>
            <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with our website</li>
            <li><strong>Functional cookies:</strong> Remember your preferences and settings</li>
            <li><strong>Marketing cookies:</strong> Used to deliver relevant advertisements, including those from Google AdSense</li>
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Information Sharing and Disclosure',
      icon: <SecurityIcon sx={{ color: 'primary.main' }} />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            We do not sell, trade, or otherwise transfer your personal information to third 
            parties without your consent, except in the following circumstances:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
            <li>With your explicit consent</li>
            <li>To comply with legal obligations</li>
            <li>To protect our rights and property</li>
            <li>In connection with a business transfer or merger</li>
            <li>With trusted third-party service providers who assist us in operating our website</li>
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Third-Party Advertising Services',
      icon: <PrivacyTipIcon sx={{ color: 'primary.main' }} />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Our website may display advertisements through Google AdSense, a third-party 
            advertising service provided by Google Inc. Google AdSense uses cookies and 
            similar technologies to collect information about your visits to our website 
            and other websites to provide you with relevant advertisements.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Important:</strong> We do not have access to or control over the 
            information collected by Google AdSense. The collection and use of this 
            information is governed by Google's Privacy Policy, which is separate from 
            our own. We encourage you to review Google's Privacy Policy to understand 
            how they collect, use, and share your information.
          </Typography>
          <Typography variant="body1" paragraph>
            You can opt out of personalized advertising by visiting{' '}
            <a 
              href="https://adssettings.google.com" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: theme.palette.primary.main }}
            >
              Google Ad Settings
            </a>
            {' '}or by visiting the{' '}
            <a 
              href="https://www.google.com/policies/technologies/ads/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: theme.palette.primary.main }}
            >
              Google Privacy Policy
            </a>.
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Data Security',
      icon: <SecurityIcon sx={{ color: 'primary.main' }} />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational security measures to protect 
            your personal information against unauthorized access, alteration, disclosure, or destruction.
          </Typography>
          <Typography variant="body1" paragraph>
            These measures include:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments and updates</li>
            <li>Access controls and authentication measures</li>
            <li>Employee training on data protection</li>
            <li>Incident response procedures</li>
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Your Rights and Choices',
      icon: <PrivacyTipIcon sx={{ color: 'primary.main' }} />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have certain rights regarding your personal information:
          </Typography>
          <Typography component="ul" variant="body1" sx={{ pl: 2 }}>
            <li><strong>Access:</strong> Request access to your personal information</li>
            <li><strong>Correction:</strong> Request correction of inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
            <li><strong>Objection:</strong> Object to certain processing activities</li>
            <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
          </Typography>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            To exercise these rights, please contact us using the information provided below.
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9 }}>
            How we protect and handle your information
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            At Teniqua Systems, we are committed to protecting your privacy and ensuring the 
            security of your personal information. This Privacy Policy explains how we collect, 
            use, disclose, and safeguard your information when you visit our website or use 
            our services.
          </Typography>
          <Typography variant="body1" paragraph>
            By using our website and services, you consent to the collection and use of 
            information in accordance with this policy. If you do not agree with our policies 
            and practices, please do not use our website.
          </Typography>
          <Typography variant="body1">
            This policy was last updated on {new Date().toLocaleDateString()}.
          </Typography>
        </Paper>

        {/* Privacy Sections */}
        <Box>
          {privacySections.map((section, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {section.icon}
                  <Typography variant="h6">
                    {section.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {section.content}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Contact Information */}
        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h4" gutterBottom>
            Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about this Privacy Policy or our data practices, 
            please contact us:
          </Typography>
          <Typography variant="body1" paragraph>
                              <strong>Email:</strong> hello@teniquasystems.com
          </Typography>

          <Typography variant="body1">
            We will respond to your inquiry within 30 days of receipt.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Privacy;
